import { useEffect, useRef, useState } from "react"
import { SketchHighlighterIcon } from "@/components/sketch/sketch-highlighter-icon"
import { SketchMarkerIcon } from "@/components/sketch/sketch-marker-icon"
import { Input } from "@/components/ui/primitives"
import { UiRange } from "@/components/ui/ui-range"
import { cn } from "@/lib/utils"

export type PenKind = "marker" | "highlighter"

export const PEN_PRESETS: Record<PenKind, { width: number; opacity: number; color: string }> = {
  marker: { width: 12, opacity: 1, color: "#0C58C8" },
  highlighter: { width: 28, opacity: 0.4, color: "#FCBC08" },
}

function tipHex(color: string): string {
  return color.startsWith("#") ? color.slice(0, 7) : "#111827"
}

type Props = {
  open: boolean
  penKind: PenKind
  color: string
  tipColors: Record<PenKind, string>
  brushWidth: number
  brushOpacity: number
  labels: {
    marker: string
    highlighter: string
    weight: string
    transparency: string
    color: string
  }
  onPenKindChange: (kind: PenKind) => void
  onColorChange: (color: string) => void
  onWidthChange: (width: number) => void
  onOpacityChange: (opacity: number) => void
  onClose: () => void
  anchorRef: React.RefObject<HTMLElement | null>
}

export function SketchPenFlyout({
  open,
  penKind,
  color,
  tipColors,
  brushWidth,
  brushOpacity,
  labels,
  onPenKindChange,
  onColorChange,
  onWidthChange,
  onOpacityChange,
  onClose,
  anchorRef,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null)
  const [weightOpen, setWeightOpen] = useState(false)

  useEffect(() => {
    if (!open) {
      setWeightOpen(false)
      return
    }
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (panelRef.current?.contains(t)) return
      if (anchorRef.current?.contains(t)) return
      onClose()
    }
    document.addEventListener("mousedown", onDoc)
    return () => document.removeEventListener("mousedown", onDoc)
  }, [open, onClose, anchorRef])

  if (!open) return null

  const tips: { kind: PenKind; label: string }[] = [
    { kind: "marker", label: labels.marker },
    { kind: "highlighter", label: labels.highlighter },
  ]

  const tipIconClass = (selected: boolean) =>
    cn(
      "pointer-events-none absolute top-1/2 right-0 h-7 w-[6.3rem] max-w-none -translate-y-1/2",
      "transition-transform duration-150 ease-out",
      "group-hover:translate-x-1.5",
      selected ? "translate-x-1.5" : "translate-x-0",
    )

  return (
    <div
      ref={panelRef}
      className="absolute top-full left-0 z-50 mt-1 flex flex-col gap-1 overflow-visible rounded-lg border border-ui-border bg-ui-bg-elevated p-1.5 shadow-xl"
    >
      {tips.map((tip) => {
        const selected = penKind === tip.kind
        const tipColor = tipHex(tipColors[tip.kind])
        return (
          <button
            key={tip.kind}
            type="button"
            title={tip.label}
            aria-label={tip.label}
            aria-pressed={selected}
            className={cn(
              "group relative h-9 w-9 cursor-pointer overflow-hidden rounded-lg outline-none",
              "focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)]",
            )}
            onClick={() => onPenKindChange(tip.kind)}
          >
            {tip.kind === "marker" ? (
              <SketchMarkerIcon color={tipColor} className={tipIconClass(selected)} />
            ) : (
              <SketchHighlighterIcon color={tipColor} className={tipIconClass(selected)} />
            )}
          </button>
        )
      })}

      <div className="my-0.5 h-px bg-ui-border" />

      <label
        className="relative flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg ring-1 ring-transparent outline-none ring-inset focus-within:ring-[var(--ui-focus-ring)] hover:bg-ui-surface-raised"
        title={labels.color}
      >
        <span
          aria-hidden
          className="h-5 w-5 rounded-full border border-black/25 shadow-sm dark:border-white/25"
          style={{ backgroundColor: tipHex(color) }}
        />
        <input
          type="color"
          value={tipHex(color)}
          className="absolute inset-0 cursor-pointer opacity-0"
          onChange={(e) => onColorChange(e.target.value)}
        />
      </label>

      <div className="relative">
        <button
          type="button"
          title={labels.weight}
          className={cn(
            "flex h-9 w-9 flex-col items-center justify-center gap-0.5 rounded-lg outline-none hover:bg-ui-surface-raised",
            "focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)]",
            weightOpen ? "bg-ui-accent/15 ring-1 ring-ui-accent ring-inset" : "ring-1 ring-transparent ring-inset",
          )}
          onClick={() => setWeightOpen((v) => !v)}
        >
          <span className="block h-0.5 w-4 rounded bg-ui-text-primary" />
          <span className="block h-1 w-4 rounded bg-ui-text-primary" />
          <span className="block h-1.5 w-4 rounded bg-ui-text-primary" />
        </button>

        {weightOpen ? (
          <div className="absolute top-0 left-full z-50 ml-2 w-56 rounded-lg border border-ui-border bg-ui-bg-elevated p-3 shadow-xl">
            <label className="mb-3 flex flex-col gap-1.5">
              <span className="text-[11px] font-medium text-ui-text-secondary">{labels.weight}</span>
              <div className="flex items-center gap-2">
                <UiRange
                  min={1}
                  max={64}
                  value={brushWidth}
                  className="min-w-0 flex-1"
                  onChange={(e) => onWidthChange(Number(e.target.value))}
                />
                <Input
                  type="number"
                  min={1}
                  max={64}
                  value={brushWidth}
                  className="h-8 w-12 px-1 text-center text-xs"
                  onChange={(e) => onWidthChange(Math.min(64, Math.max(1, Number(e.target.value) || 1)))}
                />
              </div>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-medium text-ui-text-secondary">{labels.transparency}</span>
              <div className="flex items-center gap-2">
                <UiRange
                  min={5}
                  max={100}
                  value={Math.round(brushOpacity * 100)}
                  className="min-w-0 flex-1"
                  onChange={(e) => onOpacityChange(Number(e.target.value) / 100)}
                />
                <Input
                  type="number"
                  min={5}
                  max={100}
                  value={Math.round(brushOpacity * 100)}
                  className="h-8 w-12 px-1 text-center text-xs"
                  onChange={(e) => onOpacityChange(Math.min(1, Math.max(0.05, (Number(e.target.value) || 5) / 100)))}
                />
              </div>
            </label>
          </div>
        ) : null}
      </div>
    </div>
  )
}
